// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useMemo, useEffect } from 'react';

import { Box } from '@mantine/core';
import type { ICellRendererParams, GridApi } from 'ag-grid-enterprise';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';

import { useMantineNotifications } from 'components/Mantine/useMantineNotifications';
import { baseGridDef, baseColumnDef } from 'helpers/ag-grid/baseColumnDef';
import { convertAgGridRequestToParams } from 'helpers/ag-grid/convertAgGridRequest';
import type { DetailTable } from 'helpers/ag-grid/DetailTables/detailTable';
import { AgGridEditable } from 'helpers/ag-grid/EditableCells/AgGridEditable';
import { NoRowsOverlay } from 'helpers/ag-grid/NoRowsOverlay';
import type { MidLevelRow } from 'helpers/ag-grid/useServerSideGrid';
import type { WithRequiredNotNull } from 'helpers/helper-types';

type Props<TData, DetailTitle extends string> = {
  // oxlint-disable-next-line typescript/no-explicit-any
  detailConfig: DetailTable<any, any, TData, DetailTitle> | undefined;
  rowHeight: number;
  extraSpace: number;
} & ICellRendererParams<MidLevelRow<TData, DetailTitle>>;

const DetailGridRendererWrapped = <TData, DetailTitle extends string>({
  detailConfig,
  rowHeight,
  extraSpace,
  api: parentApi,
  parentData,
}: WithRequiredNotNull<Props<TData, DetailTitle>, 'detailConfig'> & {
  parentData: TData;
}) => {
  const { showError } = useMantineNotifications();
  const [gridApi, setGridApi] = React.useState<GridApi | null>(null);

  useEffect(() => {
    if (!gridApi) return undefined;
    const id = `detail_${detailConfig.title}`;
    parentApi.addDetailGridInfo(id, {
      id,
      api: gridApi,
    });
    return () => {
      if (!parentApi.isDestroyed()) parentApi.removeDetailGridInfo(id);
    };
  }, [detailConfig.title, gridApi, parentApi]);

  const detailColDef =
    typeof detailConfig.colDef === 'function' ? detailConfig.colDef(parentData) : detailConfig.colDef;
  const props = useMemo(
    () =>
      ({
        ...baseGridDef,
        context: detailConfig.context,
        rowHeight,
        className: detailConfig.hideHeader ? 'ag-no-header' : undefined,
        headerHeight: detailConfig.hideHeader ? 0 : rowHeight,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        defaultColDef: {
          ...baseColumnDef,
          suppressHeaderContextMenu: true,
          contextMenuItems: [],
        },
        columnDefs: [
          // Spacer, probably not the right way to do this
          {
            width: extraSpace,
            resizable: false,
            sortable: false,
            lockPosition: 'left',
            enableCellChangeFlash: false,
            valueFormatter: () => '',
          },
          ...detailColDef,
        ],
        noRowsOverlayComponent: () => (
          <NoRowsOverlay pt={6} px={extraSpace + 14} pr={0} align="flex-start" label="No results found." />
        ),
        onGridReady: ({ api: detailApi }) => {
          setGridApi(detailApi);
          detailApi.setGridOption('serverSideDatasource', {
            getRows: async ({ request, success }) => {
              detailApi.hideOverlay();
              const { data: rowData, entireCount: rowCount } = await detailConfig
                .fetchPage(...convertAgGridRequestToParams(request, detailConfig.configMapper(parentData, request)))
                .catch((err) => {
                  showError(err);
                  throw err;
                });
              success({
                rowData,
                rowCount,
              });
              if (rowCount === 0) detailApi.showNoRowsOverlay();
            },
          });
        },
      }) as const satisfies AgGridReactProps<typeof detailConfig>,
    [detailColDef, detailConfig, extraSpace, parentData, rowHeight, showError],
  );

  return (
    <Box mih={rowHeight} p={2}>
      {detailConfig.editing?.editable ? (
        <AgGridEditable gridApi={gridApi} {...detailConfig.editing} withContainer={false}>
          <AgGridReact {...props} />
        </AgGridEditable>
      ) : (
        <AgGridReact {...props} />
      )}
    </Box>
  );
};

export const DetailGridRenderer = <TData, DetailTitle extends string>({
  detailConfig,
  data,
  ...rest
}: Props<TData, DetailTitle>) => {
  if (!detailConfig || !data?.parentRowData) return null;
  return (
    <DetailGridRendererWrapped {...rest} detailConfig={detailConfig} data={data} parentData={data.parentRowData} />
  );
};
